import React, { useState } from 'react';
import { useReligionSettings, ReligionType, ReligionIntent } from '@/hooks/useReligionSettings';
import ReligionEntryScreen from '@/components/religion/ReligionEntryScreen';
import ReligionSelectionScreen from '@/components/religion/ReligionSelectionScreen';
import ReligionIntentGate from '@/components/religion/ReligionIntentGate';
import ReligionDiscoveryScreen from '@/components/religion/ReligionDiscoveryScreen';
import BottomNav from '@/components/BottomNav';
import { AlertTriangle } from 'lucide-react';

type ReligionStep = 'entry' | 'selection' | 'intent' | 'discovery';

const Religion: React.FC = () => {
  const { settings, loading, isBanned, banInfo, selectReligion, setIntent, updateWaliNote } = useReligionSettings();
  const [step, setStep] = useState<ReligionStep>('entry');
  const [selectedReligion, setSelectedReligion] = useState<ReligionType | null>(null);
  const [selectedIntent, setSelectedIntent] = useState<ReligionIntent | null>(null);
  const [waliNote, setWaliNote] = useState<string | undefined>();
  const [isProcessing, setIsProcessing] = useState(false);

  // Determine initial step based on existing settings
  React.useEffect(() => {
    if (!loading && settings) {
      if (settings.religion && settings.intent) {
        setSelectedReligion(settings.religion);
        setSelectedIntent(settings.intent);
        setWaliNote(settings.wali_note || undefined);
        setStep('discovery');
      } else if (settings.religion) {
        setSelectedReligion(settings.religion);
        // For Muslim, require intent selection
        // For others, skip to discovery with default intent
        if (settings.religion === 'muslim') {
          setStep('intent');
        } else {
          // Auto-set exploring intent for non-Muslim and go to discovery
          setSelectedIntent('exploring');
          setStep('discovery');
        }
      }
    }
  }, [loading, settings]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50 via-orange-50 to-rose-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Show ban screen if user is banned
  if (isBanned && banInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50 via-orange-50 to-rose-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 flex flex-col items-center justify-center p-6 pb-20">
        <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 max-w-sm w-full shadow-lg text-center">
          <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-foreground mb-2">Access Restricted</h2>
          <p className="text-muted-foreground mb-4">
            Your access to the Religion tab has been temporarily restricted.
          </p>
          <p className="text-sm text-muted-foreground mb-2">
            <strong>Reason:</strong> {banInfo.reason}
          </p>
          {banInfo.expires_at && (
            <p className="text-sm text-muted-foreground">
              <strong>Expires:</strong> {new Date(banInfo.expires_at).toLocaleDateString()}
            </p>
          )}
        </div>
        <BottomNav />
      </div>
    );
  }

  const handleReligionSelect = async (religion: ReligionType) => {
    setIsProcessing(true);
    const { error } = await selectReligion(religion);
    setIsProcessing(false);
    
    if (!error) {
      setSelectedReligion(religion);
      
      // Muslim requires intent selection
      if (religion === 'muslim') {
        setStep('intent');
      } else {
        // Non-Muslim: auto-set exploring intent and go directly to discovery
        const intentResult = await setIntent('exploring');
        if (!intentResult.error) {
          setSelectedIntent('exploring');
          setStep('discovery');
        }
      }
    }
  };

  const handleIntentSelect = async (intent: ReligionIntent, note?: string) => {
    setIsProcessing(true);
    const { error } = await setIntent(intent);
    setIsProcessing(false);
    
    if (!error) {
      setSelectedIntent(intent);
      if (note) {
        await updateWaliNote(note);
        setWaliNote(note);
      }
      setStep('discovery');
    }
  };

  return (
    <div className="pb-16">
      {step === 'entry' && (
        <ReligionEntryScreen onContinue={() => setStep('selection')} />
      )}

      {step === 'selection' && (
        <ReligionSelectionScreen
          onBack={() => setStep('entry')}
          onSelect={handleReligionSelect}
          selectedReligion={selectedReligion || undefined}
          isLoading={isProcessing}
        />
      )}

      {step === 'intent' && selectedReligion && (
        <ReligionIntentGate
          religion={selectedReligion}
          onBack={() => setStep('selection')}
          onSelectIntent={handleIntentSelect}
        />
      )}

      {step === 'discovery' && selectedReligion && selectedIntent && (
        <ReligionDiscoveryScreen
          religion={selectedReligion}
          intent={selectedIntent}
          waliNote={waliNote}
          onBack={() => setStep('selection')}
        />
      )}

      <BottomNav />
    </div>
  );
};

export default Religion;
